import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  ArrowLeft,
  Upload,
  FileText,
  Trash2,
  Download,
  ExternalLink,
  Save,
  Image,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function MedicalPersonDetailPage() {
  const { personId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [person, setPerson] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const [formDate, setFormDate] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [hasForm, setHasForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [personRes, filesRes] = await Promise.all([
        axios.get(`${API}/medical/persons/${personId}`, { headers }),
        axios.get(`${API}/medical/persons/${personId}/files`, { headers }),
      ]);
      setPerson(personRes.data);
      setFiles(filesRes.data);

      const mf = personRes.data.medical_form || {};
      setHasForm(!!mf.has_form);
      setFormDate(mf.form_date ? mf.form_date.split("T")[0] : "");
      setExpirationDate(
        mf.expiration_date ? mf.expiration_date.split("T")[0] : ""
      );
    } catch {
      toast.error("Failed to load person data");
    } finally {
      setLoading(false);
    }
  }, [personId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveForm = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/medical/persons/${personId}/form`,
        {
          has_form: hasForm,
          form_date: formDate || null,
          expiration_date: expirationDate || null,
        },
        { headers }
      );
      toast.success("Medical form updated");
      fetchData();
    } catch {
      toast.error("Failed to update form");
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;
    setUploading(true);
    let successCount = 0;
    try {
      for (const file of selectedFiles) {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(`${API}/medical/persons/${personId}/upload`, fd, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        });
        successCount++;
      }
      toast.success(`${successCount} file${successCount > 1 ? "s" : ""} uploaded`);
      fetchData();
    } catch {
      toast.error(`Uploaded ${successCount}/${selectedFiles.length} — some failed`);
      fetchData();
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleViewFile = async (fileRecord) => {
    try {
      const response = await axios.get(
        `${API}/medical/files/${fileRecord.id}/download`,
        { headers, responseType: "blob", timeout: 60000 }
      );
      const contentType =
        response.headers["content-type"] || "application/octet-stream";
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch {
      toast.error("Failed to open file");
    }
  };

  const handleDownloadFile = async (fileRecord) => {
    try {
      const response = await axios.get(
        `${API}/medical/files/${fileRecord.id}/download`,
        { headers, responseType: "blob", timeout: 60000 }
      );
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileRecord.original_filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch {
      toast.error("Download failed");
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await axios.delete(`${API}/medical/files/${fileId}`, { headers });
      toast.success("File deleted");
      fetchData();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
      </div>
    );
  }

  if (!person) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-500">Person not found</p>
      </div>
    );
  }

  const mf = person.medical_form || {};

  return (
    <div className="space-y-6" data-testid="medical-person-detail">
      <button
        onClick={() => navigate("/medical/persons")}
        className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700"
        data-testid="medical-back-button"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Persons
      </button>

      {/* Person Header */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              {person.name}
            </h1>
            <div className="mt-2 flex flex-wrap gap-3 text-sm text-slate-500">
              <span className="capitalize px-2 py-0.5 bg-teal-50 text-teal-700 rounded-full text-xs font-medium">
                {person.person_type}
              </span>
              {person.division && (
                <span>
                  Division: <strong>{person.division}</strong>
                </span>
              )}
              {person.position && <span>Position: {person.position}</span>}
              {person.email && <span>{person.email}</span>}
              {person.phone && <span>{person.phone}</span>}
            </div>
          </div>
          <div className="text-right">
            {mf.has_form ? (
              mf.is_expired ? (
                <span className="px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-800">
                  Expired
                </span>
              ) : (
                <span className="px-3 py-1 rounded-full text-sm font-semibold bg-emerald-100 text-emerald-800">
                  Current
                </span>
              )
            ) : (
              <span className="px-3 py-1 rounded-full text-sm font-semibold bg-amber-100 text-amber-800">
                Missing Form
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Medical Form Data */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <h2 className="font-heading font-semibold text-lg text-slate-900">
          Physical Part 2 — Form Details
        </h2>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={hasForm}
              onChange={(e) => setHasForm(e.target.checked)}
              className="rounded border-slate-300"
              data-testid="medical-has-form-checkbox"
            />
            Has Physical Part 2 form on file
          </label>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">
              Form Date (Date of Physical)
            </label>
            <Input
              type="date"
              value={formDate}
              onChange={(e) => {
                setFormDate(e.target.value);
                if (e.target.value) {
                  const dt = new Date(e.target.value);
                  dt.setFullYear(dt.getFullYear() + 1);
                  setExpirationDate(dt.toISOString().split("T")[0]);
                }
              }}
              data-testid="medical-form-date-input"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">
              Expiration Date (auto: 1 year)
            </label>
            <Input
              type="date"
              value={expirationDate}
              onChange={(e) => setExpirationDate(e.target.value)}
              data-testid="medical-expiration-date-input"
            />
          </div>
        </div>
        <Button
          onClick={handleSaveForm}
          disabled={saving}
          className="bg-teal-700 hover:bg-teal-800"
          data-testid="medical-save-form-button"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Saving..." : "Save Form Status"}
        </Button>
      </div>

      {/* Uploaded Files */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-semibold text-lg text-slate-900">
            Uploaded Files ({files.length})
          </h2>
          <label
            className="cursor-pointer"
            data-testid="medical-upload-file-button"
          >
            <Button variant="outline" size="sm" disabled={uploading} asChild>
              <span>
                <Upload className="h-4 w-4 mr-2" />
                {uploading ? "Uploading..." : "Upload File"}
              </span>
            </Button>
            <input
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              multiple
              onChange={handleFileUpload}
            />
          </label>
        </div>

        {files.length === 0 ? (
          <p className="text-sm text-slate-400 py-4 text-center">
            No files uploaded yet
          </p>
        ) : (
          <div className="space-y-2">
            {files.map((f) => (
              <div
                key={f.id}
                className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-md text-sm border border-slate-200 hover:border-teal-300 transition-colors"
                data-testid={`medical-file-item-${f.id}`}
              >
                {f.content_type?.startsWith("image") ? (
                  <Image className="h-4 w-4 text-teal-700 flex-shrink-0" />
                ) : (
                  <FileText className="h-4 w-4 text-teal-700 flex-shrink-0" />
                )}
                <span className="truncate flex-1 text-slate-700 font-medium">
                  {f.original_filename}
                </span>
                <span className="text-xs text-slate-400 flex-shrink-0 hidden sm:inline">
                  {new Date(f.created_at).toLocaleDateString()}
                  {f.size && ` · ${(f.size / 1024).toFixed(0)} KB`}
                  {f.page_count && ` · ${f.page_count} pg`}
                </span>
                <button
                  onClick={() => handleViewFile(f)}
                  className="text-teal-700 hover:text-teal-900 p-1 flex items-center gap-1 text-xs font-medium rounded hover:bg-teal-50"
                  title="View file"
                  data-testid={`medical-view-file-${f.id}`}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">View</span>
                </button>
                <button
                  onClick={() => handleDownloadFile(f)}
                  className="text-emerald-600 hover:text-emerald-800 p-1 flex items-center gap-1 text-xs font-medium rounded hover:bg-emerald-50"
                  title="Download file"
                  data-testid={`medical-download-file-${f.id}`}
                >
                  <Download className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Download</span>
                </button>
                <button
                  onClick={() => handleDeleteFile(f.id)}
                  className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50"
                  title="Remove file"
                  data-testid={`medical-delete-file-${f.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
